import copyJson from '../../demo/fixtures/w3/copy.json'
import rulesJson from '../../demo/fixtures/w3/rules.json'
import upliftJson from '../../demo/fixtures/w3/uplift.json'
import copyW4Json from '../../demo/fixtures/w4/copy.json'
import type { AutopilotGoal, AutopilotState, ResultMetric, Uplift } from '../../demo/types'
import { Icon, SparkShape, UpliftStat } from '../../ui'
import { fmtDelta, formatGoalTarget, isImprovement, thirdLabelForMetric } from './autopilot'
import styles from './AutopilotSection.module.css'

interface GoalCopy { key: AutopilotGoal; label: string }
interface CopyFixture { badgeOn: string; switchOn: string; switchOff: string; checkNow: string; offBanner: string }
interface RulesFixture { goals: GoalCopy[]; nextCheckLabel: string }
interface UpliftFixture { emptyText: string }
interface CopyW4Fixture { engineStateOff: string; engineStateOn: string; engineTitle: string }

const copy = copyJson as unknown as CopyFixture
const rules = rulesJson as unknown as RulesFixture
const upliftCopy = upliftJson as unknown as UpliftFixture
const copyW4 = copyW4Json as unknown as CopyW4Fixture

interface AutopilotHeadProps {
  autopilot: AutopilotState
  resultMetric: ResultMetric
  optimizationCount: number
  uplift: Uplift | null
  running: boolean
  onRun: () => void
  onToggle: () => void
}

function toneFor(value: number): 'up' | 'down' | 'plain' {
  return value > 0 ? 'up' : value < 0 ? 'down' : 'plain'
}

export function AutopilotHead({ autopilot, resultMetric, optimizationCount, uplift, running, onRun, onToggle }: AutopilotHeadProps) {
  if (!autopilot.on) {
    return (
      <div className={`${styles.head} ${styles.headOff}`} data-scope="ap-head">
        <span className={`${styles.avatar} ${styles.avatarOff}`}><Icon name="sparkle" size={22} strokeWidth={1.7} /></span>
        <div className={styles.headCopy}>
          <p className={styles.engineTitle}>
            {copyW4.engineTitle}
            <span className={`${styles.stateChip} ${styles.stateChipOff}`}><i />{copyW4.engineStateOff}</span>
          </p>
          <p className={styles.offBanner}>{copy.offBanner}</p>
        </div>
        <button type="button" className={`${styles.switchControl} ${styles.switchControlOff}`} role="switch" aria-checked="false" onClick={onToggle}>
          <span className={styles.switchTrack}><span className={styles.switchThumb} /></span><span>{copy.switchOff}</span>
        </button>
      </div>
    )
  }

  const goal = rules.goals.find((item) => item.key === autopilot.goal)?.label ?? autopilot.goal
  const thirdLabel = uplift?.thirdLabel ?? thirdLabelForMetric(resultMetric)
  const ctrTone = uplift ? toneFor(uplift.ctrDelta) : 'plain'
  const thirdImproves = uplift ? isImprovement(uplift.thirdKey, uplift.thirdDelta) : false
  const thirdTone = uplift ? (uplift.thirdDelta === 0 ? 'plain' : thirdImproves ? 'up' : 'down') : 'plain'
  return (
    <div className={styles.head} data-scope="ap-head">
      <span className={styles.avatar}><Icon name="sparkle" size={22} strokeWidth={1.7} /></span>
      <div className={styles.headCopy}>
        <p className={styles.engineTitle}>
          {copyW4.engineTitle}
          <span className={styles.stateChip}><i />{copyW4.engineStateOn}</span>
        </p>
        <p className={styles.headGoal}>目标 {goal} {formatGoalTarget(autopilot.goal, autopilot.target)} · 下次检查 {rules.nextCheckLabel}</p>
      </div>
      <div className={styles.headMetrics} data-scope="ap-uplift">
        <UpliftStat label="自动调整" value={String(uplift?.runs ?? optimizationCount)} />
        <span className={`${styles.statWrap} ${!uplift ? styles.emptyStat : ''} ${ctrTone === 'down' ? styles.downStat : ''}`}><UpliftStat label="点击率" value={uplift ? fmtDelta(uplift.ctrDelta) : '—'} tone={ctrTone === 'up' ? 'up' : 'plain'} /></span>
        <span className={`${styles.statWrap} ${!uplift ? styles.emptyStat : ''} ${thirdTone === 'down' ? styles.downStat : ''}`}><UpliftStat label={thirdLabel} value={uplift ? fmtDelta(uplift.thirdDelta) : '—'} tone={thirdTone === 'up' ? 'up' : 'plain'} /></span>
        {uplift ? <span className={styles.spark}><SparkShape values={uplift.spark} width={140} height={38} tone="ai" /></span> : <span className={styles.upliftEmpty}>{upliftCopy.emptyText}</span>}
        <button type="button" className={styles.runButton} disabled={running} onClick={onRun}><Icon name="sparkle" size={15} />{copy.checkNow}</button>
        <button type="button" className={`${styles.switchControl} ${styles.switchControlOn}`} role="switch" aria-checked="true" onClick={onToggle}>
          <span className={styles.switchTrack}><span className={styles.switchThumb} /></span><span>{copy.switchOn}</span>
        </button>
      </div>
    </div>
  )
}
